import { Avatar, Box, CircularProgress, Dialog, DialogContent, Stack } from '@mui/material';
import { Link } from 'react-router-dom';
import CustomText from 'components/common/CustomText/CustomText';
import { useGetProducts } from 'hooks/useProduct';
import { getTranslations } from 'common/utils';
import { colors } from 'styles/colors';

interface Props {
  open: boolean;
  tagId: string | null;
  tagName: string;
  onClose: () => void;
}

// Every product wearing one tag.
//
// A tag is shared and its wording is not one product's to change, so the
// question a reviewer about to reword one asks is which products they are
// rewording. Shown here rather than by sending them to the products table: the
// answer is read and the reviewer goes back to what they were doing, and a
// screen they had to navigate away from is a screen they have to find again.
export default function TagProductsModal({ open, tagId, tagName, onClose }: Props) {
  const { data, isLoading } = useGetProducts(
    { page: 1, size: 100, tagId: tagId ?? undefined },
    { enabled: open && Boolean(tagId), refetchOnWindowFocus: false },
  );

  const products = data?.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent>
        <CustomText text={tagName || 'Tag'} fontSize={18} fontWeight={700} />
        <Box mt={0.5}>
          <CustomText
            text={
              isLoading
                ? 'Reading the products with this tag…'
                : `${products.length} ${products.length === 1 ? 'product carries' : 'products carry'} this tag`
            }
            fontSize={13}
            color={colors.gray}
          />
        </Box>

        {isLoading ? (
          <Box mt={3} display="flex" justifyContent="center">
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Stack mt={2} gap={1.5}>
            {products.map((product: any) => {
              const { en, ar } = getTranslations(product.translations);

              return (
                <Box
                  key={product.id}
                  component={Link}
                  to={`/products/all-products/${product.id}`}
                  onClick={onClose}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1,
                    borderRadius: 1.5,
                    textDecoration: 'none',
                    border: `1px solid ${colors.lightgray || '#e0e0e0'}`,
                    '&:hover': { bgcolor: colors.topBackground },
                  }}
                >
                  <Avatar
                    src={product.avatar?.small}
                    sx={{ width: 34, height: 34, borderRadius: '5px', bgcolor: colors.avatarBg }}
                  />
                  <Box minWidth={0}>
                    <CustomText text={en?.name || product.barcode} fontSize={14} fontWeight={600} />
                    <CustomText text={ar?.name || ''} fontSize={12} color={colors.gray} />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
